import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import '../../DashboardHome/knowledge.css';

const ICN = {
    search: (
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
    caret: (
        <svg viewBox="0 0 10 10" fill="none">
            <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    plus: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    upload: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 11V4m0 0L4 7m3-3l3 3M2 2h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    download: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 2v7m0 0l-3-3m3 3l3-3M2 11v1h10v-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    layers: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M7 2l5 2.5L7 7 2 4.5 7 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
            <path d="M2 7.5L7 10l5-2.5M2 10l5 2.5L12 10" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
    ),
    bolt: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path d="M8 1L3 8h3l-1 5 5-7H7l1-5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
        </svg>
    ),
    clock: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.3" />
            <path d="M7 4v3l2 1.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
    ),
    globe: (
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.3" />
            <path d="M2 7h10M7 2c1.8 1.6 1.8 8.4 0 10M7 2c-1.8 1.6-1.8 8.4 0 10" stroke="currentColor" strokeWidth="1.3" />
        </svg>
    ),
};

const PAIRS = ['Any pair'];
const THRESH = ['≥ 70%', '≥ 80%', '≥ 90%', '100% only'];
const SORT = ['Match %', 'Most used', 'Recently edited'];

const threshNum = (s) => parseInt((s || '').match(/(\d+)/)?.[1] || 70, 10);

const hl = (text, q) => {
    if (!q.trim()) return text;
    const i = (text || '').toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return text;
    return (
        <>
            {text.slice(0, i)}
            <mark>{text.slice(i, i + q.length)}</mark>
            {text.slice(i + q.length)}
        </>
    );
};

const fmt = (n) => (n || 0).toLocaleString();

const Rail = ({ memories, active, setActive }) => (
    <aside className="kb-rail">
        <div className="kb-rail-h">Memories<span className="ct">{memories.length}</span></div>
        <button
            type="button"
            className={`kb-rail-item ${active === 'all' ? 'on' : ''}`}
            onClick={() => setActive('all')}
        >
            <span className="kb-rail-flag">ALL</span>
            <span className="kb-rail-body">
                <span className="kb-rail-name">All memories</span>
                <span className="kb-rail-sub">Every pair · {fmt(memories.reduce((s, m) => s + m.n, 0))}</span>
            </span>
        </button>
        {memories.map((m) => (
            <button
                key={m.id}
                type="button"
                className={`kb-rail-item ${active === m.id ? 'on' : ''}`}
                onClick={() => setActive(m.id)}
            >
                <span className="kb-rail-flag">{m.flag}</span>
                <span className="kb-rail-body">
                    <span className="kb-rail-name">{m.name}</span>
                    <span className="kb-rail-sub">{m.pair} · {fmt(m.n)}</span>
                </span>
            </button>
        ))}
        <div className="kb-rail-divide" />
        <button type="button" className="kb-rail-add">{ICN.plus} New memory</button>
    </aside>
);

const Stats = ({ total, memoryCount, languageCount }) => (
    <div className="kb-stats">
        <div className="kb-stat">
            <div className="kb-stat-lbl"><span className="ic">{ICN.layers}</span>Total segments</div>
            <div className="kb-stat-val">{total >= 1000 ? `${(total / 1000).toFixed(1)}k` : total}</div>
            <div className="kb-stat-sub">across {memoryCount} memories · {languageCount} languages</div>
        </div>
        <div className="kb-stat">
            <div className="kb-stat-lbl"><span className="ic green">{ICN.bolt}</span>Memory coverage</div>
            <div className="kb-stat-val">{total ? Math.min(99, Math.round(total / 10)) : 0}<span className="kb-stat-unit">%</span></div>
            <div className="kb-stat-sub">recall rate across recent projects</div>
        </div>
        <div className="kb-stat">
            <div className="kb-stat-lbl"><span className="ic indigo">{ICN.globe}</span>Languages</div>
            <div className="kb-stat-val">{languageCount}</div>
            <div className="kb-stat-sub">distinct language pairs in memory</div>
        </div>
        <div className="kb-stat">
            <div className="kb-stat-lbl"><span className="ic yellow">{ICN.clock}</span>Last update</div>
            <div className="kb-stat-val">live</div>
            <div className="kb-stat-sub">auto-updates as segments are approved</div>
        </div>
    </div>
);

const Bar = ({ q, setQ, facets, cycle, pairs }) => (
    <div className="kb-bar">
        <div className="kb-search">
            {ICN.search}
            <input placeholder="Concordance search — find any source or target…" value={q} onChange={(e) => setQ(e.target.value)} />
            <span className="kbd">/</span>
        </div>
        {[['pair', 'Pair', pairs], ['thresh', 'Match', THRESH], ['sort', 'Sort', SORT]].map(([k, label, opts]) => {
            const v = facets[k] || opts[0];
            const applied = k !== 'sort' && v !== opts[0];
            return (
                <button
                    key={k}
                    type="button"
                    className={`kb-facet ${applied ? 'applied' : ''}`}
                    onClick={() => cycle(k, opts)}
                >
                    <span className="lbl-k">{label}:</span>
                    <span className="lbl-v">{v}</span>
                    {ICN.caret}
                </button>
            );
        })}
    </div>
);

const List = ({ rows, q, total, page, setPage, pageSize }) => {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    return (
        <div className="kb-tbl">
            <div className="kb-tm-row head">
                <span>Match</span>
                <span>Source</span>
                <span>Target</span>
                <span className="r">Context · usage</span>
            </div>
            {rows.length === 0 && (
                <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-3)' }}>
                    No matches yet. Translate something to populate your memory.
                </div>
            )}
            {rows.map((e) => (
                <div className="kb-tm-row body" key={e.id}>
                    <div className="kb-tm-match">
                        <span className={`kb-tm-pct ${e.cls}`}>{e.pct}%</span>
                        <span className="kb-tm-pair">{e.pair}</span>
                    </div>
                    <div className="kb-tm-cell src">
                        <span className="lang">source</span>
                        <div className="kb-tm-text">{hl(e.src, q)}</div>
                    </div>
                    <div className="kb-tm-cell">
                        <span className="lang">{e.lang} · target</span>
                        <div className="kb-tm-text">{e.tgt}</div>
                    </div>
                    <div className="kb-tm-meta">
                        {e.ctx && <span className="kb-tm-ctx"><span className="dot" /><span>{e.ctx}</span></span>}
                        <span className="kb-tm-usage">used <b>{e.usage}×</b></span>
                    </div>
                </div>
            ))}
            <div className="kb-foot">
                <span className="ct">Showing <b>{rows.length}</b> of <b>{fmt(total)}</b> segments</span>
                <span className="spacer" />
                <button type="button" className="pg-btn" disabled={page === 0} onClick={() => setPage(page - 1)}>‹ Prev</button>
                {Array.from({ length: Math.min(totalPages, 4) }).map((_, i) => (
                    <span key={i} className={`pg-num ${i === page ? 'on' : ''}`} onClick={() => setPage(i)} role="button" tabIndex={0}>
                        {i + 1}
                    </span>
                ))}
                <button type="button" className="pg-btn" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next ›</button>
            </div>
        </div>
    );
};

const TranslationMemoryPage = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [active, setActive] = useState('all');
    const [q, setQ] = useState('');
    const [facets, setFacets] = useState({ pair: 'Any pair', thresh: '≥ 70%', sort: 'Match %' });
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({ memories: [], entries: [], total: 0, filteredTotal: 0 });

    const pairs = useMemo(
        () => ['Any pair', ...data.memories.map((m) => m.pair.replace(' → ', '→'))],
        [data.memories],
    );

    const cycle = (k, opts) => {
        setFacets((f) => {
            const i = opts.indexOf(f[k] || opts[0]);
            return { ...f, [k]: opts[(i + 1) % opts.length] };
        });
        setPage(0);
    };

    useEffect(() => {
        if (!token) return;
        setLoading(true);
        const pair = facets.pair === 'Any pair'
            ? ''
            : (active !== 'all' ? data.memories.find((m) => m.id === active)?.pair.replace(' → ', '->').toLowerCase() : facets.pair.replace('→', '->').toLowerCase());
        const params = new URLSearchParams({
            q,
            pair: pair || '',
            minMatch: threshNum(facets.thresh),
            page,
            limit: 25,
        });
        axios
            .get(`/api/translation-memory/browse?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setData(r.data);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load translation memory.');
                    setLoading(false);
                }
            });
    }, [token, q, facets, page, active, data.memories.length, dispatch, navigate]);

    const languageCount = new Set(data.memories.map((m) => m.pair)).size;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Translation memory</h1>
                <span className="rs-page-title-count">{fmt(data.total)}</span>
                <div className="rs-page-actions">
                    <button type="button" className="btn-q">{ICN.upload}<span>Import TMX</span></button>
                    <button type="button" className="btn-q">{ICN.download}<span>Export</span></button>
                </div>
            </div>
            <p className="rs-page-sub">Every translation your team has shipped — searchable, leverage-tracked.</p>

            <Stats total={data.total} memoryCount={data.memories.length} languageCount={languageCount} />

            <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16, alignItems: 'start' }}>
                <Rail memories={data.memories} active={active} setActive={(id) => { setActive(id); setPage(0); }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Bar q={q} setQ={(v) => { setQ(v); setPage(0); }} facets={facets} cycle={cycle} pairs={pairs} />
                    {loading ? (
                        <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading entries…</div>
                    ) : error ? (
                        <div style={{ padding: 40, color: 'crimson' }}>{error}</div>
                    ) : (
                        <List rows={data.entries} q={q} total={data.filteredTotal || data.total} page={page} setPage={setPage} pageSize={25} />
                    )}
                </div>
            </div>
        </>
    );
};

export default TranslationMemoryPage;
